/// <reference path="../../Validation/IValidator.ts" />
/// <reference path="../../global.d.ts" />

namespace MACRAIL.Forms.Fields {

export class BaseField {

    protected element: JQuery;

    protected statusElement: JQuery;

    protected name: string;

    protected opts: FieldOptions;

    protected enabled: boolean = true;

    protected defaultValue: any = undefined;

    protected dirty: boolean = false;

    protected value: any;

    protected validators: MACRAIL.Validation.IValidator[] = [];

    protected disabledValidators: MACRAIL.Validation.IValidator[] = [];

    protected validationMessages: ValidationMessageCollection = {};

    protected validationSlugs: string[] = [];

    constructor(element: string | JQuery, opts: any = {}) {
        this.element = $(element);

        // HACK: randomly jumping about the DOM and using the tabbedform class.
        this.statusElement = this.element.closest('.tabbedform-form-alert-container');

        if (this.element.length === 0) {
            throw 'Unable to find element for field: ' + element;
        }

        this.name = this.element.prop('id');

        this.element.addClass('clean');

        this.opts = opts;

        if (this.opts.hasOwnProperty('defaultValue')) {
            this.defaultValue = this.opts.defaultValue;
        } else {
            this.defaultValue = this.getValue();
        }
    }

    public getElement(): JQuery {
        return this.element;
    }

    public setName(newName: string): void {
        this.name = newName;
    }

    public getName(): string {
        return this.name;
    }

    public addValidator(validator: MACRAIL.Validation.IValidator): void {
        this.validators.push(validator);
    }

    public getValidators(): MACRAIL.Validation.IValidator[] {
        return this.validators;
    }

    public replaceValidator(name: string, opts: any) {
        let newValidator: MACRAIL.Validation.IValidator =
            new MACRAIL.Validation[`${name}Validator`](opts);

        for (let validator of this.validators) {
            if (validator instanceof MACRAIL.Validation[`${name}Validator`]) {
                let elementPos = this.validators.indexOf(validator);
                this.validators.splice(elementPos, 1);

                this.addValidator(newValidator);
            }
        }
    }

    public disableValidator(name: string): void {
        for (let validator of this.validators) {
            if (validator instanceof MACRAIL.Validation[`${name}Validator`]) {
                this.disabledValidators.push(validator);
                let elementPos = this.validators.indexOf(validator);
                this.validators.splice(elementPos, 1);
            }
        }
    }

    public enableValidator(name: string): void {
        for (let validator of this.disabledValidators) {
            if (validator instanceof MACRAIL.Validation[`${name}Validator`]) {
                this.addValidator(validator);

                let elementPos = this.disabledValidators.indexOf(validator);
                this.disabledValidators.splice(elementPos, 1);
            }
        }
    }

    public validate(force: boolean): void {
        let existingSlugs = this.validationSlugs.slice();
        let newSlugs: string[] = [];
        let hasWarnings = false;
        let hasErrors = false;

        if (force) {
            this.setDirty(true);
        }

        if (!this.isDirty()) {
            return;
        }

        for (let validator of this.validators) {
            let messages = validator.validate(this.getValue());

            if (messages.length > 0) {
                for (let message of messages) {
                    if (message.status === 'error') {
                        hasErrors = true;
                    } else {
                        hasWarnings = true;
                    }

                    if (!message.message || message.message === '') {
                        this.clearMessage(message.slug);
                    } else {
                        this.setMessage(message.slug, message.status, message.message);
                    }
                    newSlugs.push(message.slug);
                }
            }
        }

        let removedSlugs = existingSlugs.filter((x) => newSlugs.indexOf(x) === -1);

        for (let slug of removedSlugs) {
            this.clearMessage(slug);
        }

        this.validationSlugs = newSlugs;

        let isAlert = this.statusElement.hasClass('alert');

        this.statusElement.removeClass('has-error has-warning');

        if (hasErrors) {
            this.statusElement.addClass('has-error');
            if (isAlert) {
                this.statusElement.setAlertClass('danger');
            }
        } else if (hasWarnings) {
            this.statusElement.addClass('has-warning');
            if (isAlert) {
                this.statusElement.setAlertClass('warning');
            }
        } else {
            if (isAlert) {
                this.statusElement.setAlertClass('default');
            }
        }
    }

    public getValidationMessages(): ValidationMessageCollection {
        return this.validationMessages;
    }

    public reset(): void {
        this.setValue(this.defaultValue);
        this.setDirty(false);

        let messagesElement = this.getMessagesElement();
        if (messagesElement) {
            messagesElement.text('');
        }

        this.statusElement.removeClass('has-warning has-error');
    }

    public isEnabled(): boolean {
        return this.enabled;
    }

    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;

        let noteElement = this.getNoteElement();

        if (enabled) {
            this.element
                .prop('disabled', false)
                .removeClass('disabled');

            if (noteElement) {
                noteElement.removeClass('disabled');
            }
        } else {
            this.element
                .prop('disabled', true)
                .addClass('disabled');

            if (noteElement) {
                noteElement.addClass('disabled');
            }

            // Why did I do this?!
            //this.setDirty(true);
        }
    }

    public setDirty(newDirty: boolean): void {
        this.dirty = newDirty;

        if (newDirty) {
            this.element.removeClass('clean');
        } else {
            this.element.addClass('clean');
        }
    }

    public isDirty(): boolean {
        return this.dirty;
    }

    public getValue() {
        return this.value;
    }

    public setValue(newValue: any): void {

        let currentValue = this.element.val();

        // HACK: This used to be triple-equals but values like 3 > '3' were causing infinite loops.
        // Does it need to be strict equals again?
        // tslint:disable-next-line:triple-equals
        if (newValue == currentValue) {
            return;
        }

        this.element.val(newValue);
        this.value = newValue;

        this.setDirty(true);

        // HACK: Programatically setting a value doesn't trigger a change event. Therefore force it here.
        // Only currently (I think) needed to detect the postcode changing to trigger the travel time check.
        // I'm sure we could make this much nicer.
        this.element.trigger('change');
    }

    public setNote(newNote: string): void {
        let noteDiv = this.getNoteElement();

        if (noteDiv) {
            noteDiv.text(newNote);
        }
    }

    public setMessage(slug: string, status: MACRAIL.Validation.ValidationStatus, message: string): void {

        this.validationMessages[slug] = {
            'message': message,
            'slug': slug,
            'status': status,
        };

        let messages = this.getMessagesElement();

        if (!messages) {
            return;
        }

        let messagesElement = messages.find(`div[data-slug="${slug}"]`);

        let state: string = status;
        if (state === 'error') {
            state = 'danger';
        }

        if (messagesElement.length) {
            messagesElement
                .removeClass('highlight-warning highlight-danger')
                .addClass(`highlight-${state}`)
                .html(message);
        } else {
            messages.append(`<div data-slug="${slug}" class="highlight-${state}">${message}</div>`);
        }
    }

    public clearMessage(slug: string): void {

        delete this.validationMessages[slug];

        let messagesElement = this.getMessagesElement();

        if (!messagesElement) {
            return;
        }

        let message = messagesElement.find(`[data-slug="${slug}"]`);

        message.remove();
    }

    public getMessages(): ValidationMessageCollection {
        return this.validationMessages;
    }

    public getLabel(): string {
        let el = this.getLabelElement();

        if (el && el.length) {
            return el.text();
        }

        return this.name;
    }

    public getLabelElement(): JQuery | undefined {
        return this.getRelatedElement('label[for="{NAME}"]');
    }

    public getNoteElement(): JQuery | undefined {
        return this.getRelatedElement('div[data-note-for="{NAME}"]');
    }

    public getMessagesElement(): JQuery | undefined {
        return this.getRelatedElement('div[data-messages-for="{NAME}"]');
    }

    private getRelatedElement(css: string): JQuery | undefined {
        if (this.name === '') {
            return undefined;
        }

        let element = $(css.replace('{NAME}', this.name));

        if (element.length !== 1) {
            return undefined;
        }

        return element;
    }
}

export interface FieldOptions {
    defaultValue?: any;
}

}

interface ValidationMessageCollection {
    [slug: string]: MACRAIL.Validation.IValidatorMessage;
}
